import React from 'react';
import { X, Music, Crown, Sparkles, Heart, Globe, Gift, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const benefits = [
    {
      icon: <Music className="w-6 h-6 text-amber-500" />,
      title: "Jornada Bíblica Completa",
      description: "Acesse toda a experiência musical da Criação ao Apocalipse"
    },
    {
      icon: <Crown className="w-6 h-6 text-amber-500" />,
      title: "Todas as Músicas",
      description: "Ouça sem limitações todas as categorias e playlists"
    },
    {
      icon: <Heart className="w-6 h-6 text-amber-500" />,
      title: "Favoritos Ilimitados",
      description: "Salve quantas músicas quiser em seus favoritos"
    },
    {
      icon: <Globe className="w-6 h-6 text-amber-500" />,
      title: "Acesso Offline",
      description: "Baixe suas músicas favoritas para ouvir sem internet"
    }
  ];

  const handleUpgradeClick = () => {
    onClose();
    navigate('/settings', { 
      state: { 
        focusOnSubscription: true,
        preferMonthly: true 
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card w-full max-w-lg rounded-2xl shadow-xl relative">
        <button
          aria-label="Fechar"
          onClick={onClose}
          className="absolute left-3 top-3 sm:left-4 sm:top-4 p-2 rounded-full bg-black/10 hover:bg-black/20 text-foreground border border-black/10 focus:outline-none focus:ring-2 focus:ring-primary min-w-[44px] min-h-[44px]"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="p-6">
          <div className="text-center mb-6">
            {/* PREÇO COMO ELEMENTO PRINCIPAL - Guideline 3.1.2 */}
            <div className="mb-2">
              <h2 className="text-3xl font-bold text-amber-600 mb-1">
                R$ 9,90
              </h2>
              <p className="text-base text-gray-700 font-semibold">
                por mês
              </p>
            </div>
            
            {/* INFORMAÇÃO DO TRIAL EM TAMANHO MENOR E SECUNDÁRIO */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1.5 mb-4">
              <Gift className="w-4 h-4 text-amber-500" />
              <span className="text-amber-700 font-normal text-xs">
                Inclui 7 dias grátis. Depois R$ 9,90/mês.
              </span>
            </div>
            
            <h3 className="text-xl font-bold mb-2 text-gray-800">
              Bíblia em Louvor Premium
            </h3>
            <p className="text-muted-foreground text-sm">
              Assinatura mensal com renovação automática
            </p>
          </div>

          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4">
                {benefit.icon}
                <div>
                  <h3 className="font-semibold">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 pt-0 space-y-3">
          <Button
            onClick={handleUpgradeClick}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
          >
            Assinar Mensal
          </Button>
          
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
          >
            Continuar com versão gratuita
          </button>

          {/* Links de Política de Privacidade e Termos de Uso - Conformidade Apple 3.1.2 */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3 justify-center text-center">
              <button
                onClick={() => { onClose(); navigate('/privacy-policy'); }}
                className="inline-flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors duration-200 font-medium"
              >
                Política de Privacidade
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <span className="hidden sm:inline text-gray-300">•</span>
              <button
                onClick={() => { onClose(); navigate('/terms-of-use'); }}
                className="inline-flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors duration-200 font-medium"
              >
                Termos de Uso (EULA)
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal; 